import { useId, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatMinor, type AdCampaignPerformance, type AdConnectionFreshness, type AdMetrics, type AdSeriesPoint } from '@agency/contracts';
import { Badge, EmptyState, Notice, SelectField, Skeleton } from '@agency/ui';
import { ApiClientError, get } from '../../lib/api.ts';
import { QueryError } from '../../lib/query-state.tsx';
import { fmtWhen } from './common.tsx';

// ---------- period ----------

export const PERIODS = [{ days: 7, label: 'Last 7 days' }, { days: 30, label: 'Last 30 days' }, { days: 90, label: 'Last 90 days' }] as const;
export type PeriodDays = (typeof PERIODS)[number]['days'];

/** The window is whole UTC days ending today, matching how the API buckets leads and revenue. */
export function periodRange(days: number): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getTime() - (days - 1) * 86_400_000);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

export function PeriodSelect({ value, onChange }: { value: PeriodDays; onChange: (d: PeriodDays) => void }) {
  return (
    <SelectField label="Period" value={String(value)} onChange={(e) => onChange(Number(e.target.value) as PeriodDays)}>
      {PERIODS.map((p) => <option key={p.days} value={p.days}>{p.label}</option>)}
    </SelectField>
  );
}

// ---------- formatting (display only; the API sends integers and exact decimal strings) ----------

export const fmtDay = (iso: string) => new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(`${iso}T00:00:00.000Z`));
export const fmtCount = (n: number) => new Intl.NumberFormat().format(n);
export const fmtRevenue = (r: { currency: string; amount: string }) => new Intl.NumberFormat(undefined, { style: 'currency', currency: r.currency }).format(Number(r.amount));

/** An unavailable figure is written out for screen readers; a bare dash is read as "dash" or nothing. */
export const NotAvailable = ({ why }: { why: string }) => <span title={why}><span aria-hidden="true">–</span><span className="ag-visually-hidden">Not available: {why}</span></span>;

export function RevenueText({ metrics }: { metrics: AdMetrics }) {
  if (metrics.revenue === null) return <NotAvailable why="revenue needs the analytics permission" />;
  if (!metrics.revenue.length) return <NotAvailable why="no won deal from these leads yet" />;
  return <>{metrics.revenue.map((r) => fmtRevenue(r)).join(' + ')}</>;
}

export const CpqlText = ({ metrics, currency }: { metrics: AdMetrics; currency: string }) => (metrics.cpqlMinor === null ? <NotAvailable why="no qualified lead yet" /> : <>{formatMinor(metrics.cpqlMinor, currency)}</>);

export const RoasText = ({ metrics }: { metrics: AdMetrics }) => (metrics.roas === null ? <NotAvailable why="needs revenue and spend in one currency" /> : <>{metrics.roas}×</>);

// ---------- strip ----------

/** `spendKnown` is false until a sync has read the provider: spend is then "not available", never a made-up zero. */
export function MetricsStrip({ metrics, currency, label, spendKnown = true }: { metrics: AdMetrics; currency: string; label: string; spendKnown?: boolean }) {
  const items: [string, React.ReactNode][] = [
    ['Spend', spendKnown ? formatMinor(metrics.spendMinor, currency) : <NotAvailable key="s" why="the provider has not been read yet" />],
    ['Leads', fmtCount(metrics.leads)],
    ['Qualified leads', fmtCount(metrics.qualifiedLeads)],
    ['Cost per qualified lead', <CpqlText key="c" metrics={metrics} currency={currency} />],
    ['Won revenue', <RevenueText key="r" metrics={metrics} />],
    ['Return on ad spend', <RoasText key="x" metrics={metrics} />],
  ];
  return (
    <div role="group" aria-label={label}>
      <dl className="ads-strip">
        {items.map(([name, value]) => (
          <div key={name} className="ads-strip__item"><dt className="ads-metric__l">{name}</dt><dd className="ads-metric__n">{value}</dd></div>
        ))}
      </dl>
    </div>
  );
}

// ---------- chart ----------

const W = 640;
const PAD = { l: 8, r: 8, t: 8, b: 22 };

function Bars({ points, pick, top, height, max, className }: { points: AdSeriesPoint[]; pick: (p: AdSeriesPoint) => number; top: number; height: number; max: number; className: string }) {
  const slot = (W - PAD.l - PAD.r) / points.length;
  const bar = Math.max(1, slot * 0.7);
  return (
    <g className={className}>
      {points.map((p, i) => {
        const v = pick(p);
        const h = max > 0 ? Math.max(v > 0 ? 1 : 0, (v / max) * height) : 0;
        return <rect key={p.date} x={PAD.l + i * slot + (slot - bar) / 2} y={top + height - h} width={bar} height={h} rx={1} />;
      })}
    </g>
  );
}

/**
 * Answers one question: is the money spent each day producing leads? Spend (top) and new leads (bottom) share the day axis and have separate
 * scales, so no dual axis is needed. The same numbers are available as a table for keyboard and screen-reader users.
 */
export function DailyChart({ points, currency, caption }: { points: AdSeriesPoint[]; currency: string; caption: string }) {
  const [table, setTable] = useState(false);
  const id = useId();
  if (!points.length) return null;
  const maxSpend = Math.max(0, ...points.map((p) => p.spendMinor));
  const maxLeads = Math.max(0, ...points.map((p) => p.leads));
  const totalSpend = points.reduce((n, p) => n + p.spendMinor, 0);
  const totalLeads = points.reduce((n, p) => n + p.leads, 0);
  const H1 = 120;
  const H2 = 48;
  const gap = 18;
  const height = PAD.t + H1 + gap + H2 + PAD.b;
  const labelIdx = points.length > 2 ? [0, Math.floor((points.length - 1) / 2), points.length - 1] : points.map((_, i) => i);
  const slot = (W - PAD.l - PAD.r) / points.length;
  return (
    <figure className="ads-chart" aria-labelledby={`${id}-cap`}>
      <figcaption id={`${id}-cap`}>{caption}<span className="ag-visually-hidden"> {formatMinor(totalSpend, currency)} spent and {totalLeads} leads from {fmtDay(points[0]!.date)} to {fmtDay(points[points.length - 1]!.date)}. A table of the daily values is available below the chart.</span></figcaption>
      <svg viewBox={`0 0 ${W} ${height}`} role="img" aria-hidden="true" focusable="false" className="ads-chart__svg">
        <text x={PAD.l} y={PAD.t + 8} className="ads-chart__label">{`Spend per day, up to ${formatMinor(maxSpend, currency)}`}</text>
        <Bars points={points} pick={(p) => p.spendMinor} top={PAD.t + 12} height={H1 - 12} max={maxSpend} className="ads-chart__spend" />
        <text x={PAD.l} y={PAD.t + H1 + gap - 4} className="ads-chart__label">{`New leads per day, up to ${maxLeads}`}</text>
        <Bars points={points} pick={(p) => p.leads} top={PAD.t + H1 + gap} height={H2} max={maxLeads} className="ads-chart__leads" />
        <line x1={PAD.l} x2={W - PAD.r} y1={PAD.t + H1 + gap + H2} y2={PAD.t + H1 + gap + H2} className="ads-chart__axis" />
        {labelIdx.map((i) => <text key={i} x={PAD.l + i * slot + slot / 2} y={height - 6} textAnchor={i === 0 ? 'start' : i === points.length - 1 ? 'end' : 'middle'} className="ads-chart__tick">{fmtDay(points[i]!.date)}</text>)}
      </svg>
      <button type="button" className="ads-chart__toggle" aria-expanded={table} aria-controls={`${id}-table`} onClick={() => setTable((v) => !v)}>{table ? 'Hide daily table' : 'Show daily table'}</button>
      {table ? (
        <div id={`${id}-table`} className="ag-table-wrap" role="region" aria-label="Daily values" tabIndex={0}>
          <table className="ag-table">
            <caption className="ag-visually-hidden">Spend and new leads per day (UTC)</caption>
            <thead><tr><th scope="col">Day</th><th scope="col" className="ag-num">Spend</th><th scope="col" className="ag-num">Clicks</th><th scope="col" className="ag-num">New leads</th></tr></thead>
            <tbody>{points.map((p) => <tr key={p.date}><th scope="row">{fmtDay(p.date)}</th><td className="ag-num">{formatMinor(p.spendMinor, currency)}</td><td className="ag-num">{fmtCount(p.clicks)}</td><td className="ag-num">{p.leads}</td></tr>)}</tbody>
          </table>
        </div>
      ) : null}
    </figure>
  );
}

// ---------- freshness ----------

/** The newest successful sync across the connections that can report; "never" is said plainly. */
export function lastSync(connections: AdConnectionFreshness[]): string | null {
  const times = connections.filter((c) => c.performanceRead && c.lastSyncAt).map((c) => c.lastSyncAt!);
  return times.length ? times.sort().at(-1)! : null;
}

export function FreshnessLine({ connections }: { connections: AdConnectionFreshness[] }) {
  const last = lastSync(connections);
  return <p className="ag-muted ads-fresh">{last ? <>Last synced <time dateTime={last}>{fmtWhen(last)}</time>.</> : 'No numbers have been synced yet.'} Lead and revenue days are in UTC; spend days follow each ad account’s time zone.</p>;
}

/** A failed or blocked sync is stated next to the numbers, which stay as they were at the last good sync. */
export function SyncProblems({ connections }: { connections: AdConnectionFreshness[] }) {
  const bad = connections.filter((c) => c.performanceRead && (c.lastError || c.status === 'EXPIRED' || c.status === 'ERROR'));
  if (!bad.length) return null;
  return (
    <Notice tone="warning" title="Numbers may be out of date" live={false}>
      <ul className="ads-list">
        {bad.map((c) => <li key={c.id}><strong>{c.name}</strong>: {c.lastError ?? `the connection is ${c.status.toLowerCase()}`}. {c.lastSyncAt ? <>Showing the numbers from the last successful sync, {fmtWhen(c.lastSyncAt)}.</> : 'Nothing has been synced yet.'}</li>)}
      </ul>
    </Notice>
  );
}

export function Notes({ notes }: { notes: string[] }) {
  return (
    <details className="ads-details ads-notes">
      <summary>How these numbers are measured</summary>
      <ul className="ads-list">{notes.map((n, i) => <li key={i}>{n}</li>)}</ul>
    </details>
  );
}

// ---------- per-campaign performance tab ----------

export function PerformanceTab({ campaignId, state, days, onDays }: { campaignId: string; state: string; days: PeriodDays; onDays: (d: PeriodDays) => void }) {
  const range = periodRange(days);
  const q = useQuery<AdCampaignPerformance, ApiClientError>({ queryKey: ['ads-performance', campaignId, days], queryFn: () => get(`/ads/campaigns/${campaignId}/performance?from=${range.from}&to=${range.to}`), enabled: state === 'PUBLISHED' });
  if (state !== 'PUBLISHED') return <EmptyState title="Not live yet" icon="send">Performance appears once the provider has acknowledged the campaign and a sync has run. Nothing is shown as zero before then.</EmptyState>;
  if (q.isLoading) return <div role="status"><span className="ag-visually-hidden">Loading performance…</span><Skeleton width="100%" height={220} /></div>;
  if (q.isError) return <QueryError error={q.error} onRetry={() => void q.refetch()} />;
  const p = q.data;
  if (!p) return null;
  const points: AdSeriesPoint[] = p.daily.map((d) => ({ date: d.date, spendMinor: d.spendMinor ?? 0, clicks: d.clicks, leads: d.leads }));
  return (
    <div className="ads-stack">
      <div className="ads-toolbar">
        <PeriodSelect value={days} onChange={onDays} />
        {p.isDemo ? <Badge tone="neutral">Demo data, not measured</Badge> : null}
      </div>
      <SyncProblems connections={[p.connection]} />
      {p.syncedAt ? null : <Notice tone="info" title="Not synced yet" live={false}>No numbers have been read from the provider for this campaign. They are left out instead of shown as zero.</Notice>}
      <section className="ads-card" aria-labelledby="ads-perf-h">
        <h2 id="ads-perf-h">Results, {fmtDay(p.range.from)} to {fmtDay(p.range.to)}</h2>
        <MetricsStrip metrics={p.metrics} currency={p.currency} label="Campaign results" spendKnown={!!p.syncedAt} />
        <FreshnessLine connections={[p.connection]} />
        {p.dataThrough ? <p className="ag-muted ads-fresh">Spend is available through {fmtDay(p.dataThrough)}; later days may still be restated by the platform.</p> : null}
      </section>
      <section className="ads-card" aria-label="Daily chart">
        <DailyChart points={points} currency={p.currency} caption="Is the money spent each day producing leads?" />
      </section>
      <Notes notes={p.notes} />
    </div>
  );
}
